import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Phone, Mail, MapPin, Clock, Send, ChevronRight } from 'lucide-react';
import { useLangPrefix, withLangPrefix } from '../hooks/useLangPrefix';

// ─────────────────────────────────────────────
// ABOUT — Le formateur Benki
// ─────────────────────────────────────────────
export const AboutPage = () => {
  const { lang } = useLangPrefix();
  const L = (p) => withLangPrefix(p, lang);
  return (
    <div className="bg-[#FAF7F0] text-[#1F2A4A]">
      {/* Section 01 — Hero biography (compact) */}
      <section className="pt-24 pb-8 md:pt-28 md:pb-10 relative">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center gap-2 text-xs text-[#4A5568] mb-3">
            <Link to="/" className="hover:text-[#C9B896] transition-colors">Accueil</Link>
            <ChevronRight className="w-3 h-3 text-[#C9B896]" />
            <span className="text-[#1F2A4A]">Le formateur</span>
          </div>

          <div className="grid grid-cols-12 gap-6 items-center">
            <div className="col-span-12 md:col-span-7">
              <h1 className="font-light text-[#1F2A4A] leading-[1.05] tracking-tight" data-testid="about-title">
                <span className="text-2xl md:text-4xl">Benki de Gamenki, </span>
                <span className="font-serif italic text-2xl md:text-4xl text-[#C9B896]">sellier &amp; garnisseur.</span>
              </h1>
            </div>
            <div className="col-span-12 md:col-span-4 md:col-start-9 flex md:justify-end">
              <div className="relative inline-block">
                <svg className="absolute inset-0 w-full h-full -m-2" viewBox="0 0 100 100" fill="none">
                  <circle cx="50" cy="50" r="48" stroke="#C9B896" strokeWidth="0.3" strokeDasharray="1.5 1.2" />
                </svg>
                <div className="relative w-[140px] h-[140px] md:w-[170px] md:h-[170px] rounded-full overflow-hidden bg-[#1F2A4A]">
                  <img
                    src="/sa/benoit-sellerie-academie-photoroom-1.png"
                    alt="Benki de Gamenki"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stitched separator */}
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <svg width="100%" height="2" viewBox="0 0 1200 2" preserveAspectRatio="none">
          <line x1="0" y1="1" x2="1200" y2="1" stroke="#C9B896" strokeWidth="1" strokeDasharray="4 4" />
        </svg>
      </div>

      {/* Section 02 — Le parcours */}
      <section className="py-6 md:py-10">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-12 gap-6">
            <div className="col-span-12 md:col-span-4">
              <h2 className="font-light text-2xl md:text-3xl leading-tight text-[#1F2A4A]">
                20&nbsp;ans <span className="font-serif italic">à façonner.</span>
              </h2>
            </div>
            <div className="col-span-12 md:col-span-7 md:col-start-6 space-y-3 text-[#4A5568] text-sm leading-relaxed font-light">
              <p>
                <strong className="text-[#1F2A4A] font-medium">BEP Métier du Cuir, option Sellerie-Garnissage</strong> en 2003. Formé chez les Compagnons du Devoir, Benki passe ensuite par les ateliers les plus exigeants du monde&nbsp;: sellerie automobile de luxe pour Lamborghini, Porsche et Ferrari, puis sellerie aéronautique chez Airbus Helicopters, et enfin sellerie nautique haut de gamme.
              </p>
              <p>
                En 2019, il lance sa chaîne YouTube <em className="font-serif">« La Sellerie tout simplement »</em>, suivie aujourd'hui par <strong className="text-[#C9B896] font-medium">plus de 4 500 abonnés</strong> et cumulant <strong className="text-[#C9B896] font-medium">3 millions de vues</strong>. La transmission devient son nouveau métier.
              </p>
              <p>
                En 2023, Sellerie Académie naît à Istres. L'objectif&nbsp;: former en quelques semaines de futurs selliers-garnisseurs autonomes, capables de lancer leur activité. <strong className="text-[#1F2A4A] font-medium">Dix-neuf artisans</strong> sont déjà passés par sa porte d'atelier.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Stitched separator */}
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <svg width="100%" height="2" viewBox="0 0 1200 2" preserveAspectRatio="none">
          <line x1="0" y1="1" x2="1200" y2="1" stroke="#C9B896" strokeWidth="1" strokeDasharray="4 4" />
        </svg>
      </div>

      {/* Section 03 — Chiffres clés */}
      <section className="py-6 md:py-10">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-[#1F2A4A]/10">
            {[
              { num: '20+', label: 'années de métier' },
              { num: '4500', label: 'abonnés YouTube' },
              { num: '3M', label: 'vues cumulées' },
              { num: '19+', label: 'élèves diplômés' },
            ].map((s, i) => (
              <div key={i} className="bg-[#FAF7F0] p-5 md:p-7">
                <p className="text-3xl md:text-4xl font-light text-[#C9B896]">{s.num}</p>
                <p className="text-[10px] uppercase tracking-[0.25em] text-[#4A5568] mt-1.5">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stitched separator */}
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <svg width="100%" height="2" viewBox="0 0 1200 2" preserveAspectRatio="none">
          <line x1="0" y1="1" x2="1200" y2="1" stroke="#C9B896" strokeWidth="1" strokeDasharray="4 4" />
        </svg>
      </div>

      {/* Section 04 — Citation + YouTube CTA */}
      <section className="py-8 md:py-12 bg-[#F4EFE3] relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.03] pointer-events-none" style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")" }} />
        <div className="relative max-w-4xl mx-auto px-6 md:px-12 text-center">
          <span className="font-serif italic text-5xl md:text-6xl text-[#C9B896]/40 leading-none">"</span>
          <blockquote className="font-serif italic font-normal text-lg md:text-2xl text-[#1F2A4A] leading-tight mt-2">
            Apprenez le métier de mes mains aux vôtres. Pas de secret, pas de raccourci — juste le temps, la patience et le geste juste.
          </blockquote>
          <p className="text-xs uppercase tracking-[0.25em] text-[#4A5568] mt-4">— Benki de Gamenki</p>

          <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-4">
            <a href="https://www.youtube.com/channel/UCul244CKahjwgP0ijKDwE2g" target="_blank" rel="noopener noreferrer">
              <Button className="bg-[#1F2A4A] text-[#FAF7F0] hover:bg-[#324166] rounded-full px-7 py-5 text-xs uppercase tracking-[0.25em] font-medium shadow-xl shadow-[#1F2A4A]/15">
                Voir la chaîne YouTube
              </Button>
            </a>
            <Link to={L('/formations-sur-place')} className="group inline-flex items-baseline gap-3 text-[#1F2A4A] border-b border-[#C9B896] pb-1 hover:gap-5 transition-all duration-500">
              <span className="text-xs uppercase tracking-[0.25em]">Voir les formations</span>
              <span className="text-[#C9B896]">→</span>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};


// ─────────────────────────────────────────────
// CONTACT
// ─────────────────────────────────────────────
export const ContactPage = () => {
  const [searchParams] = useSearchParams();
  const [form, setForm] = useState({ name: '', email: '', phone: '', subject: '', message: '' });
  const [submitting, setSubmitting] = useState(false);

  // Pre-fill subject/message from query params (e.g. "Demander une session" CTA)
  useEffect(() => {
    const subject = searchParams.get('subject');
    const message = searchParams.get('message');
    if (subject || message) {
      setForm((prev) => ({
        ...prev,
        subject: subject || prev.subject,
        message: message || prev.message,
      }));
    }
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error('Merci de remplir les champs obligatoires');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error('send failed');
      toast.success('Message envoyé ! Benki vous répond sous 48 h.');
      setForm({ name: '', email: '', phone: '', subject: '', message: '' });
    } catch {
      toast.error("L'envoi a échoué. Réessayez ou écrivez-nous à assistance.sellerie@gmail.com");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-[#FAF7F0] text-[#1F2A4A]">
      {/* Hero — compact */}
      <section className="pt-32 pb-4 md:pt-40 md:pb-6">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center gap-2 text-xs text-[#4A5568] mb-3">
            <Link to="/" className="hover:text-[#C9B896] transition-colors">Accueil</Link>
            <ChevronRight className="w-3 h-3 text-[#C9B896]" />
            <span className="text-[#1F2A4A]">Contact</span>
          </div>
          <h1 className="font-light text-[#1F2A4A] leading-[1.05] tracking-tight text-2xl md:text-4xl max-w-4xl" data-testid="contact-title">
            <span>Une question, </span>
            <span className="font-serif italic text-[#C9B896]">un projet&nbsp;?</span>
          </h1>
          <p className="text-[#4A5568] text-sm font-light max-w-xl mt-2 leading-relaxed">
            Benki et son équipe répondent personnellement sous 48&nbsp;heures. Pour un échange immédiat, appelez-nous.
          </p>
        </div>
      </section>

      {/* Stitched separator */}
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <svg width="100%" height="2" viewBox="0 0 1200 2" preserveAspectRatio="none">
          <line x1="0" y1="1" x2="1200" y2="1" stroke="#C9B896" strokeWidth="1" strokeDasharray="4 4" />
        </svg>
      </div>

      {/* Form + Coordonnées */}
      <section className="py-4 md:py-6">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-12 gap-8">
            {/* FORM */}
            <form onSubmit={handleSubmit} className="col-span-12 md:col-span-7 space-y-4" data-testid="contact-form">
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label className="text-[10px] font-mono tracking-[0.2em] text-[#C9B896] uppercase">Nom *</Label>
                  <Input
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="bg-transparent border-0 border-b border-[#1F2A4A]/20 rounded-none px-0 focus:border-[#C9B896] focus-visible:ring-0 text-[#1F2A4A] text-sm font-light h-9"
                    placeholder="Votre nom"
                    data-testid="contact-name"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-[10px] font-mono tracking-[0.2em] text-[#C9B896] uppercase">Email *</Label>
                  <Input
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="bg-transparent border-0 border-b border-[#1F2A4A]/20 rounded-none px-0 focus:border-[#C9B896] focus-visible:ring-0 text-[#1F2A4A] text-sm font-light h-9"
                    placeholder="vous@email.com"
                    data-testid="contact-email"
                  />
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label className="text-[10px] font-mono tracking-[0.2em] text-[#C9B896] uppercase">Téléphone</Label>
                  <Input
                    type="tel"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    className="bg-transparent border-0 border-b border-[#1F2A4A]/20 rounded-none px-0 focus:border-[#C9B896] focus-visible:ring-0 text-[#1F2A4A] text-sm font-light h-9"
                    placeholder="+33 ..."
                    data-testid="contact-phone"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-[10px] font-mono tracking-[0.2em] text-[#C9B896] uppercase">Sujet</Label>
                  <Input
                    value={form.subject}
                    onChange={(e) => setForm({ ...form, subject: e.target.value })}
                    className="bg-transparent border-0 border-b border-[#1F2A4A]/20 rounded-none px-0 focus:border-[#C9B896] focus-visible:ring-0 text-[#1F2A4A] text-sm font-light h-9"
                    placeholder="À propos de..."
                    data-testid="contact-subject"
                  />
                </div>
              </div>
              <div className="space-y-1">
                <Label className="text-[10px] font-mono tracking-[0.2em] text-[#C9B896] uppercase">Votre message *</Label>
                <Textarea
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  className="bg-transparent border-0 border-b border-[#1F2A4A]/20 rounded-none px-0 focus:border-[#C9B896] focus-visible:ring-0 text-[#1F2A4A] text-sm font-light min-h-[110px] py-2 resize-none"
                  placeholder="Racontez-nous votre projet, vos questions..."
                  data-testid="contact-message"
                />
              </div>
              <div className="pt-2">
                <Button
                  type="submit"
                  disabled={submitting}
                  className="bg-[#1F2A4A] text-[#FAF7F0] hover:bg-[#324166] rounded-full px-7 py-5 text-xs uppercase tracking-[0.25em] font-medium shadow-xl shadow-[#1F2A4A]/15"
                  data-testid="contact-submit"
                >
                  {submitting ? 'Envoi...' : (<><Send className="w-3.5 h-3.5 mr-2" /> Envoyer le message</>)}
                </Button>
              </div>
            </form>

            {/* COORDONNÉES */}
            <aside className="col-span-12 md:col-span-4 md:col-start-9">
              <ul className="space-y-3">
                {[
                  { icon: MapPin, label: 'Atelier', value: '25 Avenue du Tubé\nZAC du Tubé Sud — Pépinière d\'entreprises\n13800 Istres, France' },
                  { icon: Phone, label: 'Téléphone', value: '+33 4 65 01 10 66', href: 'tel:+33465011066' },
                  { icon: Mail, label: 'Email', value: 'assistance.sellerie@gmail.com', href: 'mailto:assistance.sellerie@gmail.com' },
                  { icon: Clock, label: 'Horaires', value: 'Lundi – Vendredi\n9 h — 18 h' },
                ].map((c, i) => {
                  const Icon = c.icon;
                  return (
                    <li key={i} className="flex gap-3 pb-3 border-b border-dashed border-[#1F2A4A]/15 last:border-b-0">
                      <Icon className="w-3.5 h-3.5 text-[#C9B896] mt-1 shrink-0" strokeWidth={1.5} />
                      <div>
                        <p className="text-[10px] uppercase tracking-[0.25em] text-[#4A5568] mb-0.5">{c.label}</p>
                        {c.href ? (
                          <a href={c.href} className="text-[#1F2A4A] hover:text-[#C9B896] text-xs transition-colors whitespace-pre-line">{c.value}</a>
                        ) : (
                          <p className="text-[#1F2A4A] text-xs whitespace-pre-line">{c.value}</p>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            </aside>
          </div>
        </div>
      </section>
    </div>
  );
};
